import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

function findDateConditions(filters: FilterConditionDTO[] | undefined): {
  from?: string;
  to?: string;
} {
  if (!filters) return {};
  let from: string | undefined;
  let to: string | undefined;
  for (const item of filters) {
    if (item.op === "and" && Array.isArray(item.value)) {
      for (const c of item.value as FilterConditionDTO[]) {
        if (c.field === "requestedAt" && typeof c.value === "string") {
          if (c.op === "gte") from = c.value.slice(0, 10);
          if (c.op === "lte") to = c.value.slice(0, 10);
        }
      }
    }
  }
  return { from, to };
}

const STATUS_OPTIONS = [
  { value: "all", label: "ທຸກສະຖານະ" },
  { value: "requested", label: "ລໍຖ້າກວດສອບ" },
  { value: "approved", label: "ອະນຸມັດແລ້ວ" },
  { value: "rejected", label: "ປະຕິເສດ" },
  { value: "refunded", label: "ຄືນເງິນແລ້ວ" },
];

export function RefundRequestsFilter() {
  const nav = useNavigate({ from: "/app/refunds" });
  const search = useSearch({ from: "/app/refunds" }) as Record<string, unknown>;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const inputRef = useRef<HTMLInputElement>(null);

  const statusFilter = findCondition(filters, "status");
  const reasonFilter = findCondition(filters, "reason");
  const dateRange = findDateConditions(filters);

  const [searchValue, setSearchValue] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>(dateRange.from ?? "");
  const [toDate, setToDate] = useState<string>(dateRange.to ?? "");

  useEffect(() => {
    setSearchValue((reasonFilter?.value as string) ?? "");
  }, [reasonFilter?.value]);

  useEffect(() => {
    setFromDate(dateRange.from ?? "");
    setToDate(dateRange.to ?? "");
  }, [dateRange.from, dateRange.to]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    const nextFilters = val
      ? upsertCondition(filters, {
          field: "reason",
          op: "contains",
          value: val,
        })
      : removeConditions(filters, "reason");
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const statusValue =
    statusFilter?.op === "eq" && typeof statusFilter?.value === "string"
      ? statusFilter.value
      : "all";

  const hasFilter =
    !!reasonFilter?.value || !!statusFilter?.value || !!fromDate || !!toDate;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            ref={inputRef}
            placeholder="ຄົ້ນຫາເຫດຜົນ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />

          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="!h-8 w-full sm:w-40"
            options={STATUS_OPTIONS}
          />

          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : "";
              const toStr = to ? format(to, "yyyy-MM-dd") : "";
              setFromDate(fromStr);
              setToDate(toStr);

              let nextFilters: FilterConditionDTO[] =
                removeConditions(filters, "requestedAt") ?? [];
              const dateConditions: FilterConditionDTO[] = [];
              if (fromStr) {
                dateConditions.push({
                  field: "requestedAt",
                  op: "gte",
                  value: `${fromStr} 00:00:00`,
                });
              }
              if (toStr) {
                dateConditions.push({
                  field: "requestedAt",
                  op: "lte",
                  value: `${toStr} 23:59:59`,
                });
              }
              if (dateConditions.length > 0) {
                nextFilters = upsertCondition(nextFilters, {
                  field: "requestedAt",
                  op: "and",
                  value: dateConditions,
                } as FilterConditionDTO);
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ຊ່ວງວັນທີຂໍ"
            className="h-8 w-full sm:w-fit"
          />
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (inputRef.current) inputRef.current.value = "";
              nav({ search: { ...search, filters: undefined, offset: 0 } });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="size-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
